import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import { Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useTerminalNotes } from "@/domains/terminals/services/account-user-terminals";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import { UserTerminal } from "@/shared/types/userTerminalTypes";

interface TerminalNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  terminal: UserTerminal;
}

const TerminalNotesDialog = ({
  open,
  onOpenChange,
  onSuccess,
  terminal,
}: TerminalNotesDialogProps) => {
  const terminalNotes = useTerminalNotes();
  const [notes, setNotes] = useState<string | null>(terminal.notes ?? '');

  useEffect(() => {
    setNotes(terminal.notes ?? '');
  }, [terminal.notes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await terminalNotes.mutateAsync({
        terminalId: terminal.userTerminalId,
        notes: notes,
      });
      showSuccess(SUCCESS.TERMINAL.NOTES);
      onSuccess();
      onOpenChange(false);
      // Reset form
      setNotes('');
    } catch (error) {
      showErrorWithApiError(error, ERROR.TERMINAL.NOTES);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Terminal Notes</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Textarea
                id="notes"
                name="notes"
                rows={10}
                placeholder="Enter Terminal Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={terminalNotes.isPending}

            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={terminalNotes.isPending}
              className="gap-2">
              {terminalNotes.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default TerminalNotesDialog; 